import { useMemo, useState } from "react";
import { BookOpen, Check, Clock3, ExternalLink, PauseCircle, UserRound, Workflow as WorkflowIcon } from "lucide-react";
import type { SlaSpec, WorkflowInstanceView } from "@ncp/api-client";
import { formatDate, useLocale, useTranslation, legalBasisText } from "@ncp/i18n";
import { Badge, Button, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, SlaRing, Tooltip, cn } from "@ncp/ui";
import type { CaseDetail, SlaClockDto } from "../api";
import { processGuideFor } from "./guides";
import { processPhases, type Phase, type PhaseKey, type PhaseStanding, type Stage, type StageStanding } from "./phases";

type Translate = ReturnType<typeof useTranslation>["t"];

const STAGE_DOT: Record<StageStanding, string> = {
  visited: "bg-success-500",
  current: "bg-aegold-600 ring-2 ring-aegold-200",
  ahead: "border border-sand-400 bg-white",
  bypassed: "border border-dashed border-sand-300 bg-transparent",
};

const PHASE_BADGE: Record<PhaseStanding, "success" | "gold" | "outline" | "default"> = { done: "success", current: "gold", ahead: "outline", skipped: "default" };

/** "10 days · extendable by 10" – the statutory duration a stage or clock is designed with. */
function durationText(days: number, workingDays: boolean, extension: number, t: Translate): string {
  const base = t(workingDays ? "time.workingDays" : "time.days", { count: days });
  return extension > 0 ? `${base} · ${t("backoffice:workspace.extendable", { days: extension })}` : base;
}

function specText(spec: SlaSpec | undefined, t: Translate): string | null {
  return spec ? durationText(spec.days, spec.workingDays, spec.maxExtensionDays, t) : null;
}

/**
 * The stages of this file, and nothing else to navigate by: each phase it passes through with its full name, the one it is in, the
 * ones it has passed (each opens the record of what was concluded there) and the ones still ahead. Under the steps, the stage the file
 * is in, the role that owns it and the nearest deadline; the full procedure map opens in a side panel.
 */
export function StageStepper({ detail, workflow, selected, onSelect }: { detail: CaseDetail; workflow?: WorkflowInstanceView; selected: PhaseKey | null; onSelect: (phase: PhaseKey) => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const [mapOpen, setMapOpen] = useState(false);
  const phases = useMemo(() => (workflow ? processPhases(workflow) : []), [workflow]);
  const current = workflow?.definition.nodes.find((n) => n.key === workflow.currentState);
  if (!workflow || !current || phases.length === 0) return null;

  const c = detail.summary;
  const closed = current.kind === "End";
  const currentPhase = phases.find((p) => p.standing === "current");
  const openClocks = detail.clocks.filter((k) => !k.completedAt).sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.dueAt.localeCompare(b.dueAt));
  const primary = openClocks[0];
  const owner = current.owner ?? current.actors[0];
  const clockText = primary ? (primary.pausedAt ? t("backoffice:workspace.paused") : primary.daysRemaining < 0 ? t("time.daysOverdue", { count: Math.abs(primary.daysRemaining) }) : t("time.daysLeft", { count: primary.daysRemaining })) : null;
  const shown = selected ?? currentPhase?.key ?? phases[phases.length - 1].key;

  return (
    <section aria-label={t("backoffice:stage.steps")} className="rounded-xl border border-sand-200 bg-white px-4 pb-3 pt-4 shadow-[0_1px_0_rgba(0,0,0,0.02)]">
      <ol className="flex items-start">
        {phases.map((p, i) => {
          const reachable = p.standing === "done" || p.standing === "current";
          const isSelected = p.key === shown;
          const stage = p.standing === "current" ? current : null;
          return (
            <li key={p.key} className="relative min-w-0 flex-1">
              {i > 0 ? <span aria-hidden className={cn("absolute top-4 h-0.5 -translate-y-1/2 end-[calc(50%+1.25rem)] start-[calc(-50%+1.25rem)]", p.standing === "done" || p.standing === "current" ? "bg-success-500" : "bg-sand-200")} /> : null}
              <button
                type="button"
                data-stage-step={p.key}
                disabled={!reachable}
                aria-current={p.standing === "current" ? "step" : undefined}
                aria-pressed={reachable ? isSelected : undefined}
                onClick={() => onSelect(p.key)}
                title={reachable ? undefined : t(p.standing === "skipped" ? "backoffice:stage.notOnPath" : "backoffice:stage.notReached")}
                className={cn("group flex w-full flex-col items-center gap-1.5 rounded-lg px-1 pb-1 text-center outline-none focus-visible:ring-2 focus-visible:ring-ring", reachable ? "cursor-pointer" : "cursor-default")}
              >
                <span className={cn(
                  "relative grid size-8 place-items-center rounded-full text-xs font-semibold transition-colors",
                  p.standing === "done" && "bg-success-600 text-white",
                  p.standing === "current" && "bg-aegold-700 text-white",
                  p.standing === "ahead" && "border-2 border-sand-300 bg-white text-sand-700",
                  p.standing === "skipped" && "border-2 border-dashed border-sand-300 bg-white text-sand-700",
                  isSelected && reachable && "ring-4 ring-aegold-200",
                )}>
                  {p.standing === "done" ? <Check className="size-4" strokeWidth={3} aria-hidden /> : i + 1}
                </span>
                {/* On a phone only the stage in view is named under its step; the others keep their name for screen readers. */}
                <span className={cn("text-sm leading-snug", isSelected && reachable ? "font-semibold text-aeblack-950 max-sm:whitespace-nowrap" : reachable ? "font-medium text-aeblack-900 group-hover:text-aegold-800 max-sm:sr-only" : "text-sand-700 max-sm:sr-only")}>
                  {t(`backoffice:workspace.phases.${p.key}`)}
                </span>
                {stage && !closed ? <span className="text-xs leading-snug text-aegold-900 max-sm:hidden">{ar ? stage.labelAr : stage.label}</span> : null}
                <span className="sr-only">{t(`backoffice:workspace.phaseStanding.${p.standing}`)}</span>
              </button>
            </li>
          );
        })}
      </ol>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-sand-100 pt-2.5 text-xs text-muted-foreground">
        {currentPhase && !closed ? <span className="font-medium text-aeblack-900">{t("backoffice:stage.stepOf", { n: phases.indexOf(currentPhase) + 1, total: phases.length })}</span> : null}
        {!closed && owner ? <span className="inline-flex items-center gap-1.5"><UserRound className="size-3.5" /> {t("backoffice:stage.owner")}: {t(`roles.${owner}`, { defaultValue: owner })}</span> : null}
        {!closed && primary && clockText ? (
          <Tooltip content={`${ar ? primary.labelAr : primary.label} · ${t("time.dueOn", { date: formatDate(primary.dueAt, locale) })}`}>
            <span className={cn("inline-flex items-center gap-1.5", primary.pausedAt ? "" : primary.daysRemaining < 0 ? "font-medium text-danger-700" : primary.health === "AtRisk" ? "text-warning-800" : "")}>
              {primary.pausedAt ? <PauseCircle className="size-3.5" /> : <Clock3 className="size-3.5" />} {clockText}
            </span>
          </Tooltip>
        ) : null}
        <button type="button" onClick={() => setMapOpen(true)} className="ms-auto inline-flex items-center gap-1 text-aegold-800 underline-offset-2 hover:underline"><WorkflowIcon className="size-3.5" /> {t("backoffice:workspace.processMap")}</button>
      </div>
      <ProcedurePanel open={mapOpen} onOpenChange={setMapOpen} detail={detail} phases={phases} clocks={openClocks} caseTitle={ar && c.titleAr ? c.titleAr : c.title} />
    </section>
  );
}

/** The procedure of this case in a side panel: each phase with its stages and where they stand, the running clocks, and the written guide. */
function ProcedurePanel({ open, onOpenChange, detail, phases, clocks, caseTitle }: { open: boolean; onOpenChange: (o: boolean) => void; detail: CaseDetail; phases: Phase[]; clocks: SlaClockDto[]; caseTitle: string }) {
  const { t } = useTranslation();
  const guide = processGuideFor(detail.summary.type);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent width="max-w-lg">
        <SheetHeader>
          <SheetTitle>{t("backoffice:workspace.processMap")}</SheetTitle>
          <SheetDescription>{caseTitle}</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          <ol className="space-y-3">
            {phases.map((p, i) => (
              <li key={p.key} className={cn("rounded-xl border p-3", p.standing === "current" ? "border-aegold-300 bg-aegold-50/50" : "border-sand-200")}>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <p className="flex items-center gap-2 text-sm font-semibold">
                    <span className={cn("grid size-6 place-items-center rounded-full text-[11px]", p.standing === "done" ? "bg-success-500 text-white" : p.standing === "current" ? "bg-aegold-600 text-white" : "bg-sand-200 text-sand-700")}>{p.standing === "done" ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}</span>
                    {t(`backoffice:workspace.phases.${p.key}`)}
                  </p>
                  <Badge size="sm" variant={PHASE_BADGE[p.standing]}>{t(`backoffice:workspace.phaseStanding.${p.standing}`)}</Badge>
                </div>
                <ol className="space-y-0.5">{p.stages.map((s) => <StageRow key={s.node.key} stage={s} />)}</ol>
              </li>
            ))}
          </ol>
          <Legend />
          {clocks.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:workspace.clocks")}</p>
              <Clocks clocks={clocks} />
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 border-t border-sand-200 pt-4">
            {guide ? (
              <Tooltip content={t("backoffice:workspace.processGuideHint")}>
                <Button size="sm" variant="outline" asChild>
                  <a href={guide} target="_blank" rel="noreferrer"><BookOpen /> {t("backoffice:workspace.processGuide")} <ExternalLink className="opacity-60" /></a>
                </Button>
              </Tooltip>
            ) : null}
          </div>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function StageRow({ stage }: { stage: Stage }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const n = stage.node;
  const spec = n.clocks?.find((s) => s.primary) ?? n.clocks?.[0];
  const facts = [t(`backoffice:workspace.stageStanding.${stage.standing}`), specText(spec, t), legalBasisText(n.legalBasis, locale)].filter(Boolean).join(" · ");
  return (
    <li className={cn("flex items-start gap-2.5 rounded-lg px-2 py-1.5", stage.standing === "current" && "bg-aegold-50")}>
      <span className={cn("mt-1.5 size-2.5 shrink-0 rounded-full", STAGE_DOT[stage.standing])} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className={cn("text-xs leading-snug", stage.standing === "bypassed" ? "text-sand-500" : stage.standing === "current" ? "font-semibold text-aegold-900" : "text-aeblack-900")}>{locale === "ar" ? n.labelAr : n.label}</p>
        <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-muted-foreground" title={facts}>{facts}</p>
      </div>
    </li>
  );
}

function Legend() {
  const { t } = useTranslation();
  const keys: StageStanding[] = ["visited", "current", "ahead", "bypassed"];
  return (
    <ul className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
      {keys.map((k) => <li key={k} className="flex items-center gap-1"><span className={cn("size-2 rounded-full", STAGE_DOT[k])} aria-hidden />{t(`backoffice:workspace.stageStanding.${k}`)}</li>)}
    </ul>
  );
}

/** The clocks still running: the primary one in full, the others as rings. */
function Clocks({ clocks }: { clocks: SlaClockDto[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const [primary, ...others] = clocks;
  const tip = (k: SlaClockDto) => [ar ? k.labelAr : k.label, t("time.dueOn", { date: formatDate(k.dueAt, locale) }), durationText(k.durationDays, k.workingDays, 0, t) + (k.extensionDays ? ` (+${k.extensionDays})` : ""), legalBasisText(k.legalBasis, locale), k.pausedAt ? t("backoffice:workspace.paused") : null].filter(Boolean).join(" · ");
  return (
    <div className="flex items-center gap-4">
      <Tooltip content={tip(primary)}>
        <div className="flex items-center gap-2.5 text-start">
          <SlaRing size={48} percent={primary.percentElapsed} daysRemaining={primary.daysRemaining} health={primary.health} />
          <span className="hidden leading-tight sm:block">
            <span className="flex items-center gap-1.5 text-xs font-medium"><span className="max-w-44 truncate">{ar ? primary.labelAr : primary.label}</span>{primary.isPrimary ? <Badge size="sm" variant="gold">{t("backoffice:workspace.primaryClock")}</Badge> : null}</span>
            <span className="block text-[11px] text-muted-foreground">{t("time.dueOn", { date: formatDate(primary.dueAt, locale) })}{primary.pausedAt ? ` · ${t("backoffice:workspace.paused")}` : ""}</span>
          </span>
        </div>
      </Tooltip>
      {others.length > 0 ? (
        <ul className="flex items-center gap-2" aria-label={t("backoffice:workspace.clocks")}>
          {others.slice(0, 3).map((k) => (
            <li key={k.id}>
              <Tooltip content={tip(k)}>
                <div className="flex items-center gap-1.5 rounded-full border border-sand-200 bg-white/80 py-0.5 pe-2.5 ps-0.5 text-start">
                  <SlaRing size={26} percent={k.percentElapsed} daysRemaining={k.daysRemaining} health={k.health} className="[&>div:last-child]:hidden" />
                  <span className="text-[11px] leading-tight"><span className="block max-w-32 truncate font-medium">{ar ? k.labelAr : k.label}</span><span className={cn("block text-muted-foreground", k.daysRemaining < 0 && "text-danger-600")}>{k.pausedAt ? t("backoffice:workspace.paused") : k.daysRemaining < 0 ? t("time.overdue") : t("time.daysLeft", { count: k.daysRemaining })}</span></span>
                </div>
              </Tooltip>
            </li>
          ))}
          {others.length > 3 ? <li className="text-[11px] text-muted-foreground">+{others.length - 3}</li> : null}
        </ul>
      ) : null}
    </div>
  );
}
